import React, { useState, useEffect } from 'react';
import {
  Show,
  useRecordContext,
  useRedirect,
  useDataProvider,
  useNotify,
} from 'react-admin';
import ResourceBreadcrumbsWithActions from '../../components/ResourceBreadcrumbsWithActions';
import { FranchisesTable } from '../../components/FranchisesTable';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Avatar,
  Divider,
  Tabs,
  Tab,
  Container,
  Paper,
  Chip
} from '@mui/material';
import {
  Store as StoreIcon,
  TrendingUp as TrendingUpIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  Palette as PaletteIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material';
import { BrandMetrics } from '../../types/franchise';
import { formatCurrency, formatPercentage, getComplianceStatus } from '../../types/franchise';
import { FranchiseDetailDialog } from '../../components/FranchiseDetailDialog';

const BrandSummaryCard = () => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  const primaryColor = record.branding?.primaryColor || '#1976d2';

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              src={record.branding?.logoUrl}
              sx={{
                width: 80,
                height: 80,
                bgcolor: primaryColor,
                fontSize: '1.5rem',
                fontWeight: 'bold',
              }}
            >
              <StoreIcon sx={{ fontSize: 40, color: 'white' }} />
            </Avatar>
          </Grid>

          <Grid item xs>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: primaryColor, mb: 1 }}>
              {record.name}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              {record.industry && (
                <Chip label={record.industry} size="small" color="primary" />
              )}
              {record.enterprise?.name && (
                <Chip label={record.enterprise.name} size="small" variant="outlined" />
              )}
            </Box>

            {record.description && (
              <Typography variant="body1" color="text.secondary">
                {record.description}
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// KPI Card Component for Brand Overview
const BrandKPICard = ({
  title,
  value,
  icon: Icon,
  color = '#1976d2',
}: {
  title: string;
  value: string | number;
  icon?: React.ComponentType<any>;
  color?: string;
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: `${color}08`,
        border: `1px solid ${color}20`,
        borderRadius: 2,
        textAlign: 'center',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
        {Icon && <Icon sx={{ color, fontSize: 24 }} />}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color }}>
        {value}
      </Typography>
    </Paper>
  );
};

const MetricsCard = ({ metrics, error }: { metrics: BrandMetrics | null; error?: string }) => {
  if (!metrics) {
    return (
      <Card>
        <CardContent>
          <Typography color={error ? "error" : "textSecondary"}>
            {error || "Loading metrics..."}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={2}>
      {/* KPI Cards Row 1 */}
      <Grid item xs={12} sm={6} md={3}>
        <BrandKPICard
          title="Total Revenue"
          value={(() => {
            const revenueM = (metrics.revenue.totalRevenue * 100) / 1000000;
            const decimals = revenueM < 10 ? 2 : 1;
            const factor = Math.pow(10, decimals);
            const roundedValue = Math.ceil(revenueM * factor) / factor;
            return `$${roundedValue.toFixed(decimals)}M`;
          })()}
          icon={MoneyIcon}
          color="#1976D2"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <BrandKPICard
          title="Total Royalties"
          value={(() => {
            const royaltiesM = (metrics.revenue.totalRoyalties * 100) / 1000000;
            const decimals = royaltiesM < 10 ? 2 : 1;
            const factor = Math.pow(10, decimals);
            const roundedValue = Math.ceil(royaltiesM * factor) / factor;
            return `$${roundedValue.toFixed(decimals)}M`;
          })()}
          icon={MoneyIcon}
          color="#F57C00"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <BrandKPICard
          title="Avg. Compliance"
          value={
            metrics.summary.averageComplianceScore !== null
              ? `${metrics.summary.averageComplianceScore.toFixed(1)}%`
              : 'N/A'
          }
          icon={AssessmentIcon}
          color="#7B1FA2"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <BrandKPICard
          title="Growth Rate"
          value={`${(metrics.summary.averageGrowthRate || 0).toFixed(1)}%`}
          icon={TrendingUpIcon}
          color="#2E7D32"
        />
      </Grid>

      {/* Invoice Metrics */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="primary" />
              Invoice Performance
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Invoices
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {metrics.invoices.total}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Paid Invoices
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {metrics.invoices.paid}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Invoice Revenue
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {formatCurrency(metrics.invoices.totalRevenue * 100)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const RegionsTab = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [regions, setRegions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!record?.id) return;

    const fetchRegions = async () => {
      try {
        const response = await dataProvider.getList('regions', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: { brandId: record.id },
        });
        setRegions(response.data);
      } catch (error) {
        console.error('Error fetching regions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRegions();
  }, [record?.id, dataProvider]);

  if (loading) {
    return <Typography>Loading regions...</Typography>;
  }

  if (regions.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No regions found for this brand
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={2}>
      {regions.map((region: any) => (
        <Grid item xs={12} md={6} key={region.id}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 3,
              },
            }}
            onClick={() => redirect(`/regions/${region.id}/show`)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <LocationIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {region.name}
                  </Typography>
                  {region.description && (
                    <Typography variant="caption" color="text.secondary">
                      {region.description}
                    </Typography>
                  )}
                </Box>
              </Box>
              {region._count && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Franchisees
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {region._count.tenants || 0}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

const BrandingTab = () => {
  const record = useRecordContext();

  if (!record?.branding) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No branding configuration found
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const branding = record.branding;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaletteIcon color="primary" />
              Brand Colors
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Primary Color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: branding.primaryColor,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.300'
                      }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {branding.primaryColor}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Secondary Color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: branding.secondaryColor,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.300'
                      }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {branding.secondaryColor}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              {branding.accentColor && (
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Accent Color
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: branding.accentColor,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.300'
                        }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {branding.accentColor}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {branding.logoUrl && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Logo
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
                <img src={branding.logoUrl} alt="Brand logo" style={{ maxHeight: 120, maxWidth: '100%' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

const SettingsTab = () => {
  const record = useRecordContext();

  if (!record?.settings) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No settings configuration found
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const settings = record.settings;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon color="primary" />
              General Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {settings.currency && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Currency
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                      {settings.currency}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {settings.timeZone && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Time Zone
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                      {settings.timeZone}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {settings.defaultRoyaltyRate !== null && settings.defaultRoyaltyRate !== undefined && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Default Royalty Rate
                    </Typography>
                    <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5, color: 'success.main' }}>
                      {formatPercentage(settings.defaultRoyaltyRate)}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {(settings.primaryTaxRate || settings.secondaryTaxRate) && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tax Configuration
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {settings.primaryTaxRate && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {settings.primaryTaxName || 'Primary Tax'}
                      </Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                        {formatPercentage(settings.primaryTaxRate)}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {settings.secondaryTaxRate && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {settings.secondaryTaxName || 'Secondary Tax'}
                      </Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                        {formatPercentage(settings.secondaryTaxRate)}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

const FranchiseesTab = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [regions, setRegions] = useState<any[]>([]);
  const [franchisees, setFranchisees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filteredLoading, setFilteredLoading] = useState(false);
  const [selectedRegionId, setSelectedRegionId] = useState<string>('');
  const [selectedFranchisee, setSelectedFranchisee] = useState<any | null>(null);

  // Fetch regions for brand
  useEffect(() => {
    if (!record?.id) return;

    const fetchRegions = async () => {
      try {
        setLoading(true);
        const regionsResult = await dataProvider.getList('regions', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: { brandId: record.id },
        });
        setRegions(regionsResult.data || []);
      } catch (error) {
        console.error('Error fetching regions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRegions();
  }, [record?.id, dataProvider]);

  // Fetch franchisees scoped to brand and optionally region
  useEffect(() => {
    if (!record?.id) return;

    const fetchFranchisees = async () => {
      try {
        setFilteredLoading(true);
        const filter: any = { brandId: record.id };

        if (selectedRegionId) {
          filter.regionId = selectedRegionId;
        }

        const result = await dataProvider.getList('tenants', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'code', order: 'ASC' },
          filter,
        });

        setFranchisees(result.data || []);
      } catch (error) {
        console.error('Error fetching franchisees:', error);
        setFranchisees([]);
      } finally {
        setFilteredLoading(false);
      }
    };

    fetchFranchisees();
  }, [record?.id, selectedRegionId, dataProvider]);

  return (
    <Box>
      {/* Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Region</InputLabel>
                <Select
                  value={selectedRegionId}
                  onChange={(e) => setSelectedRegionId(e.target.value)}
                  label="Region"
                >
                  <MenuItem value="">All Regions</MenuItem>
                  {regions.map((region) => (
                    <MenuItem key={region.id} value={region.id}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Franchisees Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Franchises ({franchisees.length})
            </Typography>
            {filteredLoading && <CircularProgress size={24} />}
          </Box>
          <FranchisesTable
            franchisees={franchisees}
            loading={filteredLoading}
            showBrand={false}
            showRegion={true}
            onRowClick={(franchisee) => setSelectedFranchisee(franchisee)}
          />
        </CardContent>
      </Card>

      {/* Franchise Detail Dialog */}
      <FranchiseDetailDialog
        open={!!selectedFranchisee}
        franchisee={selectedFranchisee}
        onClose={() => setSelectedFranchisee(null)}
      />
    </Box>
  );
};

const BrandShowContent = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [activeTab, setActiveTab] = useState(0);
  const [metrics, setMetrics] = useState<BrandMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState<string | null>(null);

  useEffect(() => {
    if (!record?.id) return;

    const fetchMetrics = async () => {
      setMetricsError(null);
      try {
        const response = await dataProvider.getOne('franchise-analytics/brand', {
          id: record.id,
        });
        setMetrics(response.data as BrandMetrics);
      } catch (error) {
        console.error('Error fetching metrics:', error);
        setMetricsError('Metrics not available');
        notify('Failed to load brand metrics', { type: 'warning' });
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, [record?.id, dataProvider, notify]);

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box>
        <BrandSummaryCard />

        <Card sx={{ mt: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2 }}
            >
              <Tab label="Overview" />
              <Tab label="Regions" />
              <Tab label="Franchises" />
              <Tab label="Branding" />
              <Tab label="Settings" />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {activeTab === 0 && (
              <Box>
                {metricsLoading ? (
                  <Typography>Loading metrics...</Typography>
                ) : (
                  <MetricsCard metrics={metrics} error={metricsError} />
                )}
              </Box>
            )}
            {activeTab === 1 && <RegionsTab />}
            {activeTab === 2 && <FranchiseesTab />}
            {activeTab === 3 && <BrandingTab />}
            {activeTab === 4 && <SettingsTab />}
          </Box>
        </Card>
      </Box>
    </Container>
  );
};

const BrandTitle = () => {
  const record = useRecordContext();
  if (!record) return 'Loading...';
  return record.name || 'Brand';
};

export const BrandShow = () => (
  <Show actions={false} title={<BrandTitle />}>
    <ResourceBreadcrumbsWithActions hideEdit={false} hideDelete={false} />
    <BrandShowContent />
  </Show>
);
